/**
 * Official exam dates (D-139), shared by the web and mobile learner spaces: the sessions verified
 * by the team near the learner, the registration deadline countdown and the registration guide.
 * The server only sends sessions a person has verified; nothing here invents or completes a date.
 */

import { NBSP } from "./money";

export interface ExamDateCard {
  id: string;
  exam: string;
  exam_name: string;
  level: string;
  city: string;
  center_name: string;
  dates: string[];
  first_date: string;
  last_date: string;
  registration_deadline: string | null;
  deadline_passed: boolean;
  mode: string;
  fee_amount: string | null;
  fee_currency: string;
  fee_noted_on: string | null;
  registration_url: string;
  source_url: string;
  source_checked_on: string;
  reminder: boolean;
}

export interface GuideStep {
  title: string;
  text: string;
  source: string;
  verify: boolean;
}

export interface GuideRule {
  text: string;
  source: string;
}

export interface ExamGuide {
  steps: GuideStep[];
  links: { label: string; url: string }[];
  retake: GuideRule | null;
  validity: GuideRule | null;
  results: GuideRule | null;
  documents: GuideRule | null;
}

const DAY_MS = 86_400_000;

/** « 2026-10-03 » → days from `today` (local calendar days, never hours). */
export function daysBetween(today: string, day: string): number {
  const a = Date.UTC(+today.slice(0, 4), +today.slice(5, 7) - 1, +today.slice(8, 10));
  const b = Date.UTC(+day.slice(0, 4), +day.slice(5, 7) - 1, +day.slice(8, 10));
  return Math.round((b - a) / DAY_MS);
}

export type DeadlineTone = "none" | "open" | "soon" | "last" | "closed";

/** The registration deadline of a session: days left and the tone of the badge
 * (soon: 7 days or less, last: the day itself or the day before, closed: over). */
export function deadlineState(card: Pick<ExamDateCard, "registration_deadline" | "deadline_passed">, today: string): {
  tone: DeadlineTone;
  daysLeft: number | null;
} {
  if (!card.registration_deadline) return { tone: "none", daysLeft: null };
  const daysLeft = daysBetween(today, card.registration_deadline);
  if (card.deadline_passed || daysLeft < 0) return { tone: "closed", daysLeft };
  if (daysLeft <= 1) return { tone: "last", daysLeft };
  if (daysLeft <= 7) return { tone: "soon", daysLeft };
  return { tone: "open", daysLeft };
}

/** The sessions still ahead, nearest first (the server already sorts; this keeps it safe). */
export function upcomingSessions<T extends Pick<ExamDateCard, "first_date" | "last_date">>(
  cards: T[],
  today: string,
  limit = 6,
): T[] {
  return cards
    .filter((c) => daysBetween(today, c.last_date) >= 0)
    .sort((a, b) => a.first_date.localeCompare(b.first_date))
    .slice(0, limit);
}

/** « 217 000 FCFA » for XAF (project format), else « 390 CAD ». Empty without a fee. */
export function sessionFee(card: Pick<ExamDateCard, "fee_amount" | "fee_currency">): string {
  if (!card.fee_amount || !card.fee_currency) return "";
  const value = Number.parseFloat(card.fee_amount);
  if (!Number.isFinite(value)) return "";
  const whole = Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  return card.fee_currency === "XAF" ? `${whole}${NBSP}FCFA` : `${whole}${NBSP}${card.fee_currency}`;
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function rule(value: unknown): GuideRule | null {
  if (!value || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  const body = text(row.text);
  return body ? { text: body, source: text(row.source) } : null;
}

/** The registration guide sent by the API (free-form object) → typed, unknown keys ignored. */
export function parseGuide(raw: Record<string, unknown> | null | undefined): ExamGuide {
  const value = raw ?? {};
  const steps = Array.isArray(value.steps) ? value.steps : [];
  const links = Array.isArray(value.links) ? value.links : [];
  return {
    steps: steps
      .filter((s): s is Record<string, unknown> => !!s && typeof s === "object")
      .map((s) => ({ title: text(s.title), text: text(s.text), source: text(s.source), verify: s.verify === true }))
      .filter((s) => s.title),
    links: links
      .filter((l): l is Record<string, unknown> => !!l && typeof l === "object")
      .map((l) => ({ label: text(l.label), url: text(l.url) }))
      .filter((l) => l.url.startsWith("https://")),
    retake: rule(value.retake),
    validity: rule(value.validity),
    results: rule(value.results),
    documents: rule(value.documents),
  };
}
